import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { AlertTriangle, ArrowLeft, BookOpen, Download, Loader2, Minus, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AbilityContext } from '../../context/casl/AbilityContext';
import AccessRestricted from '../../components/AccessRestricted';
import { Can } from '../../components/Can';
import { LoadingPageSkeleton } from '../../components/common/LoadingPageSkeleton';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { cn } from '../../components/ui/utils';
import { AssignmentPeopleModal } from '../../components/learning/AssignmentPeopleModal';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { learningApi } from '../../utils/learning-api';
import { generateLearningCertificatePdf } from '../../utils/learning-certificate-pdf';
import {
    assignmentSourceLabel,
    courseAssignmentStatusPillClass,
    courseContentTypeLabel,
    durationLabel,
    formatLearningDate,
    initials,
} from '../../utils/learning';
import type { AssignmentDetail, AssignmentRow, Course } from '../../types/learning.types';

/**
 * Assignment detail (LEARNING.md §7.2) — one course, its snapshot, and every learner on it.
 *
 * `course.visibility` is the LIVE rule set (the course record), while `isOnboardingCourse` /
 * `issuesCertificate` shown in the snapshot line are the values frozen onto each row when it
 * was assigned. The header states both so nobody reads a later course edit back into history.
 *
 * Certificate column: `AssignmentRow` now carries the certificate id, number and expiry
 * (LearningService.serializeAssignment attaches them in one batch), so a row with an issued
 * certificate shows its number and downloads the PDF directly. The PDF is rendered
 * client-side from that record (LEARNING.md §13.3).
 */

const StatusPills: React.FC<{ row: AssignmentRow }> = ({ row }) => (
    <>
        <span
            className={cn(
                'inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium',
                courseAssignmentStatusPillClass(row.status, row.overdue),
            )}
        >
            {row.status}
        </span>
        {row.overdue && (
            <span className="ml-1 inline-block whitespace-nowrap rounded-full border border-destructive/30 bg-destructive/5 px-2 py-0.5 text-[11px] font-medium text-destructive">
                Overdue
            </span>
        )}
    </>
);

const LearnerCell: React.FC<{ row: AssignmentRow }> = ({ row }) => (
    <div className="flex items-center gap-2">
        <Avatar className="h-7 w-7">
            <AvatarFallback className="text-[10px] font-semibold">
                {initials(row.userName)}
            </AvatarFallback>
        </Avatar>
        <span className="leading-tight">
            <span className="block">{row.userName}</span>
            <span className="block text-[11px] text-muted-foreground">
                {row.userJobRole ?? '–'}
            </span>
        </span>
    </div>
);

const CertificateCell: React.FC<{ row: AssignmentRow }> = ({ row }) => {
    const { certificateId, certificateNumber, certificateExpiryDate, completedDate, courseTitle, id, userName } = row;
    if (!row.certificateIssued || certificateId == null) {
        return <span className="text-muted-foreground">–</span>;
    }
    return (
        <div className="flex items-center gap-2">
            <span className="font-mono text-xs">{certificateNumber ?? 'Issued'}</span>
            <Button
                variant="outline"
                size="icon"
                className="h-7 w-7 text-emerald-600"
                title="Download certificate"
                onClick={() => void generateLearningCertificatePdf(
                    {
                        id: certificateId,
                        certificateNumber: certificateNumber ?? '',
                        issuedDate: completedDate,
                        expiryDate: certificateExpiryDate,
                        courseTitle,
                        courseAssignmentId: id,
                    },
                    userName,
                )}
            >
                <Download size={14} />
            </Button>
        </div>
    );
};

/** "Everyone", or the scoped departments / job roles the live course record allows. */
const visibilitySummary = (course: Course): string => {
    const rules = course.visibility ?? [];
    if (rules.length === 0) return 'Everyone';
    const parts = rules.map((rule) => {
        if (rule.visibilityType === 'Department') return rule.departmentCode ?? 'Department';
        if (rule.visibilityType === 'JobRole') return rule.jobRole ?? 'Job role';
        return 'Everyone';
    });
    return Array.from(new Set(parts)).join(', ');
};

export default function AssignmentDetailPage() {
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();
    const { courseId } = useParams<{ courseId: string }>();

    const numericCourseId = Number(courseId);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.COURSE_ASSIGNMENT);

    const [detail, setDetail] = useState<AssignmentDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [peopleMode, setPeopleMode] = useState<'add' | 'remove' | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<AssignmentRow | null>(null);
    const [blockedRow, setBlockedRow] = useState<AssignmentRow | null>(null);
    const [deleting, setDeleting] = useState(false);

    const fetchDetail = useCallback(async () => {
        if (!Number.isFinite(numericCourseId) || numericCourseId <= 0) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const response = await learningApi.assignments.detail(numericCourseId);
            setDetail(response);
        } catch (error) {
            console.error('Get assignment detail error:', error);
        } finally {
            setLoading(false);
        }
    }, [numericCourseId]);

    useEffect(() => {
        if (canRead) fetchDetail();
    }, [canRead, fetchDetail]);

    const handleDeleteClick = (row: AssignmentRow) => {
        if (row.deletable) {
            setDeleteTarget(row);
            return;
        }
        setBlockedRow(row);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            setDeleting(true);
            await learningApi.assignments.remove(deleteTarget.id);
            toast.success('Assignment deleted');
            setDeleteTarget(null);
            await fetchDetail();
        } catch (error) {
            console.error('Delete assignment error:', error);
        } finally {
            setDeleting(false);
        }
    };

    if (!canRead) return <AccessRestricted />;

    if (loading && !detail) {
        return (
            <div className="p-2 md:p-0">
                <LoadingPageSkeleton />
            </div>
        );
    }

    if (!detail) {
        return (
            <div className="p-2 md:p-0">
                <Card className="p-8 text-center text-sm text-muted-foreground">
                    This assignment could not be loaded.
                </Card>
            </div>
        );
    }

    const { course, stats, rows } = detail;

    const statTiles = [
        { label: 'Assigned to', value: stats.total, valueClassName: '' },
        { label: 'Not started', value: stats.assigned, valueClassName: '' },
        { label: 'In progress', value: stats.inProgress, valueClassName: 'text-amber-600' },
        { label: 'Completed', value: stats.completed, valueClassName: 'text-emerald-600' },
        { label: 'Overdue', value: stats.overdue, valueClassName: 'text-destructive' },
    ];

    return (
        <div className="space-y-4 p-2 md:space-y-6 md:p-0">
            {/* ─── Breadcrumb ─── */}
            <div className="flex items-center gap-3">
                <Button
                    variant="outline"
                    size="icon"
                    aria-label="Back to Learning Register"
                    onClick={() => navigate('/learning/register')}
                >
                    <ArrowLeft size={16} />
                </Button>
                <p className="text-sm text-muted-foreground">
                    Learning Register / <span className="text-foreground">Assignment</span>
                </p>
            </div>

            {/* ─── Course header ─── */}
            <Card className="gap-0 p-4 md:p-5">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div className="flex items-start gap-3">
                        <div
                            className="flex size-14 shrink-0 items-center justify-center rounded-lg"
                            style={{ backgroundColor: course.category?.colour ?? '#64748b' }}
                        >
                            <BookOpen size={24} className="text-white" />
                        </div>
                        <div className="min-w-0">
                            <h1 className="text-xl font-bold tracking-tight">{course.title}</h1>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                {course.category && (
                                    <span
                                        className="rounded-full border bg-card px-2 py-0.5 text-[11px] font-semibold"
                                        style={{
                                            color: course.category.colour,
                                            borderColor: course.category.colour,
                                        }}
                                    >
                                        {course.category.name}
                                    </span>
                                )}
                                <span className="text-xs text-muted-foreground">
                                    {courseContentTypeLabel(course.contentType)} ·{' '}
                                    {durationLabel(course.estimatedDurationMinutes)} · Visible to{' '}
                                    {visibilitySummary(course)}
                                </span>
                            </div>
                            <p className="mt-2 font-mono text-[11px] text-muted-foreground">
                                Snapshotted at assignment time · isOnboardingCourse:{' '}
                                {String(course.showInOnboarding)} · issuesCertificate:{' '}
                                {String(course.issuesCertificate)}
                            </p>
                        </div>
                    </div>

                    <Can I={ACTIONS.UPDATE} a={RESOURCES.COURSE_ASSIGNMENT}>
                        <div className="flex flex-col gap-2 sm:flex-row">
                            <Button
                                variant="outline"
                                className="w-full sm:w-auto"
                                onClick={() => setPeopleMode('add')}
                            >
                                <Plus size={16} />
                                Add People
                            </Button>
                            <Button
                                variant="outline"
                                className="w-full sm:w-auto"
                                onClick={() => setPeopleMode('remove')}
                            >
                                <Minus size={16} />
                                Remove People
                            </Button>
                        </div>
                    </Can>
                </div>

                {/* ─── Stats ─── */}
                <div className="mt-5 grid grid-cols-2 gap-3 border-t border-border pt-4 sm:grid-cols-3 lg:grid-cols-5">
                    {statTiles.map((tile) => (
                        <div key={tile.label}>
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                {tile.label}
                            </div>
                            <div className={cn('text-xl font-bold', tile.valueClassName)}>
                                {tile.value}
                            </div>
                        </div>
                    ))}
                </div>
            </Card>

            {/* ─── Assigned learners ─── */}
            <Card className="gap-0 overflow-hidden">
                <div className="border-b border-border px-4 py-3 text-sm font-semibold">
                    Assigned learners
                </div>

                {/* Mobile cards */}
                <div className="space-y-3 p-4 md:hidden">
                    {rows.length === 0 ? (
                        <p className="py-8 text-center text-sm text-muted-foreground">
                            Nobody is assigned to this course yet.
                        </p>
                    ) : (
                        rows.map((row) => (
                            <div
                                key={row.id}
                                className="rounded-lg border border-border p-3"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <LearnerCell row={row} />
                                    <span className="shrink-0">
                                        <StatusPills row={row} />
                                    </span>
                                </div>
                                <p className="mt-2 text-xs text-muted-foreground">
                                    {assignmentSourceLabel(row)} · Due{' '}
                                    {formatLearningDate(row.dueDate)}
                                </p>
                                <div className="mt-3 flex items-center justify-between gap-2">
                                    <span className="text-xs">
                                        <CertificateCell row={row} />
                                    </span>
                                    <Can I={ACTIONS.DELETE} a={RESOURCES.COURSE_ASSIGNMENT}>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className={cn(
                                                row.deletable
                                                    ? 'text-destructive'
                                                    : 'text-muted-foreground',
                                            )}
                                            onClick={() => handleDeleteClick(row)}
                                        >
                                            <Trash2 size={16} />
                                            Delete
                                        </Button>
                                    </Can>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Desktop table */}
                <div className="hidden md:block">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="font-semibold text-slate-900">Learner</TableHead>
                                <TableHead className="font-semibold text-slate-900">Source</TableHead>
                                <TableHead className="font-semibold text-slate-900">Due</TableHead>
                                <TableHead className="font-semibold text-slate-900">Status</TableHead>
                                <TableHead className="font-semibold text-slate-900">Certificate</TableHead>
                                <TableHead className="font-semibold text-slate-900 text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={6}
                                        className="px-3 py-16 text-center text-sm text-muted-foreground"
                                    >
                                        Nobody is assigned to this course yet.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell className="whitespace-nowrap">
                                            <LearnerCell row={row} />
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap text-muted-foreground">
                                            {assignmentSourceLabel(row)}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap text-muted-foreground">
                                            {formatLearningDate(row.dueDate)}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap">
                                            <StatusPills row={row} />
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap">
                                            <CertificateCell row={row} />
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap">
                                            <div className="flex justify-end">
                                                <Can I={ACTIONS.DELETE} a={RESOURCES.COURSE_ASSIGNMENT}>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        title={
                                                            row.deletable
                                                                ? 'Delete assignment'
                                                                : `${row.userName} has started this course — the row is a training record`
                                                        }
                                                        className={cn(
                                                            row.deletable
                                                                ? 'text-destructive'
                                                                : 'text-muted-foreground',
                                                        )}
                                                        onClick={() => handleDeleteClick(row)}
                                                    >
                                                        <Trash2 size={16} />
                                                        Delete
                                                    </Button>
                                                </Can>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </Card>

            <AssignmentPeopleModal
                open={peopleMode !== null}
                onOpenChange={(open) => {
                    if (!open) setPeopleMode(null);
                }}
                courseId={numericCourseId}
                courseTitle={course.title}
                mode={peopleMode ?? 'add'}
                alreadyAssignedIds={rows.map((row) => row.userId)}
                onChanged={fetchDetail}
            />

            {/* ─── Delete confirmation ─── */}
            <Dialog
                open={deleteTarget !== null}
                onOpenChange={(open) => {
                    if (!open && !deleting) setDeleteTarget(null);
                }}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete this assignment?</DialogTitle>
                        <DialogDescription>
                            {deleteTarget?.userName}'s assignment for "{course.title}" will be
                            removed. This can't be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteTarget(null)}
                            disabled={deleting}
                        >
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={confirmDelete} disabled={deleting}>
                            {deleting && <Loader2 size={16} className="animate-spin" />}
                            Delete assignment
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ─── Blocked dialog (LEARNING.md §12.6) ─── */}
            <Dialog
                open={blockedRow !== null}
                onOpenChange={(open) => {
                    if (!open) setBlockedRow(null);
                }}
            >
                <DialogContent className="sm:max-w-lg">
                    <div className="flex items-start gap-3">
                        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-amber-50">
                            <AlertTriangle size={18} className="text-amber-600" />
                        </div>
                        <DialogHeader>
                            <DialogTitle>Can't delete this assignment</DialogTitle>
                            <DialogDescription>
                                {blockedRow?.userName}'s row is{' '}
                                <strong className="text-foreground">{blockedRow?.status}</strong>.
                                Once a learner opens a course the row becomes a training record —
                                deletion is only permitted while the status is{' '}
                                <strong className="text-foreground">Assigned</strong>.
                            </DialogDescription>
                        </DialogHeader>
                    </div>

                    <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs leading-relaxed text-muted-foreground">
                        Pre-flight blocker check, same as the app's archive/delete convention.
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setBlockedRow(null)}>
                            Close
                        </Button>
                        <Button variant="destructive" disabled>
                            Delete assignment
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
